import { Injectable, NgZone, inject } from '@angular/core';
import { disableDebugTools } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { ConsoleLog } from 'src/app/user-identification/models/user-identification';

type DisableDevtoolApi = ((options?: Record<string, unknown>) => { success?: boolean; reason?: string }) & {
	isDevToolOpened?: () => boolean;
};

const CONSOLE_MUTE_METHODS = ['log', 'info', 'debug', 'table'] as const;

/** Mismo intervalo que la lib: disuasión sin saturar el hilo principal. */
const DEVTOOLS_POLL_INTERVAL_MS = 2000;

/** Query para saltar la disuasión sin cambiar environment ni redeploy: `?devtools=1`. */
export const DEVTOOLS_BYPASS_QUERY_PARAM = 'devtools';
export const DEVTOOLS_BYPASS_QUERY_VALUE = '1';

/**
 * True si la protección debe aplicarse (flag de entorno ON y sin bypass por URL).
 * Usado también desde `main.ts` antes del bootstrap.
 */
export function isDevToolsProtectionEnabled(): boolean {
	if (!environment.devtoolsProtection) {
		return false;
	}
	if (typeof window === 'undefined') {
		return false;
	}
	try {
		return new URLSearchParams(window.location.search).get(DEVTOOLS_BYPASS_QUERY_PARAM) !== DEVTOOLS_BYPASS_QUERY_VALUE;
	} catch {
		return true;
	}
}

@Injectable({
	providedIn: 'root',
})
export class DevToolsProtectionService {
	private readonly errorService = inject(ErrorService);
	private readonly cloudLogsService = inject(CloudLogsService);
	private readonly ngZone = inject(NgZone);

	private activated = false;
	/** Un solo cloud log por sesión de página. */
	private devtoolsLogSent = false;
	/**
	 * Estado estable open/close (no usar isDevToolOpened de la lib en Continuar:
	 * entre ticks resetea flags y puede dar false un instante → flash del journey).
	 */
	private devToolsOpen = false;

	/** Activa disuasión de DevTools (idempotente; no-op si el flag está apagado o hay bypass URL). */
	public activate(): void {
		if (!isDevToolsProtectionEnabled() || this.activated) {
			return;
		}
		this.activated = true;
		this.disableAngularDebugTools();
		// Arrancar la lib ANTES de mutear console.*; varios detectores dependen de log/table reales.
		void this.startDisableDevtool();
	}

	/** Opciones de `disable-devtool` (expuesto para tests). */
	public buildDisableDevtoolOptions(): Record<string, unknown> {
		return {
			disableMenu: true,
			clearLog: true,
			interval: DEVTOOLS_POLL_INTERVAL_MS,
			ondevtoolopen: (type: unknown, _next: () => void) => {
				this.devToolsOpen = true;
				this.showBlockedOverlay(typeof type === 'number' ? type : undefined);
			},
			ondevtoolclose: () => {
				this.devToolsOpen = false;
			},
		};
	}

	/**
	 * Continuar: solo cierra el overlay si DevTools ya no están abiertas.
	 * ErrorService no conoce la lib → action por callback (evita dependencia circular).
	 */
	public tryContinueAfterDevToolsCheck(): void {
		if (this.devToolsOpen) {
			return;
		}
		this.errorService.clearError();
	}

	private showBlockedOverlay(detectorType?: number): void {
		this.ngZone.run(() => {
			const alreadyShowing = this.errorService.isDevToolsBlockedError();
			this.errorService.setDevToolsBlockedError(() => this.tryContinueAfterDevToolsCheck());
			if (!alreadyShowing) {
				this.sendDevToolsOpenedLog(detectorType);
			}
		});
	}

	private sendDevToolsOpenedLog(detectorType?: number): void {
		if (this.devtoolsLogSent) {
			return;
		}
		this.devtoolsLogSent = true;
		this.cloudLogsService.sendCloudLogs(
			{
				devtoolsOpened: true,
				...(detectorType !== undefined ? { detectorType } : {}),
			},
			ConsoleLog.DEVTOOLS_OPENED,
			LogType.Warning,
		);
	}

	private muteInformativeConsole(): void {
		const noop = (): void => undefined;
		for (const method of CONSOLE_MUTE_METHODS) {
			console[method] = noop;
		}
	}

	private disableAngularDebugTools(): void {
		try {
			disableDebugTools();
		} catch {
			// En builds de producción las debug tools ya pueden no estar registradas.
		}
	}

	private async startDisableDevtool(): Promise<void> {
		try {
			const module = await import('disable-devtool');
			const disableDevtool = (module.default ?? module) as DisableDevtoolApi;
			if (typeof disableDevtool !== 'function') {
				console.warn('[DevToolsProtection] disable-devtool no exporta una función usable');
				return;
			}
			const result = disableDevtool(this.buildDisableDevtoolOptions());
			if (result?.success === false) {
				console.warn('[DevToolsProtection] disable-devtool no arrancó:', result.reason);
				return;
			}
			this.muteInformativeConsole();
			// Ya abiertas al cargar: fijar estado y overlay (no confiar solo en el siguiente tick).
			if (disableDevtool.isDevToolOpened?.()) {
				this.devToolsOpen = true;
				this.showBlockedOverlay();
			}
		} catch (err) {
			console.warn('[DevToolsProtection] no se pudo cargar disable-devtool', err);
		}
	}
}
